import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AccountAccessDenied } from '../components/accounts/AccountAccessDenied';
import { AccountActivityFeed } from '../components/accounts/AccountActivityFeed';
import { AccountDetailProfile } from '../components/accounts/AccountDetailProfile';
import {
  AccountTransferModal,
  pendingTransferForClient,
} from '../components/accounts/AccountTransferModal';
import { AccountDeliveriesPanel } from '../components/accounts/AccountDeliveriesPanel';
import { AccountDetailRelatedLists } from '../components/accounts/AccountDetailRelatedLists';
import { AccountSubCompaniesPanel } from '../components/accounts/AccountSubCompaniesPanel';
import { AccountDetailSidebar } from '../components/accounts/AccountDetailSidebar';
import { AccountFormModal } from '../components/accounts/AccountFormModal';
import { AccountIntelPanel } from '../components/accounts/AccountIntelPanel';
import { AccountLogoUpload } from '../components/accounts/AccountLogoUpload';
import { AccountAcraStatus } from '../components/accounts/AccountAcraStatus';
import { acraStatusForClient, useAccountAcraStatusMap } from '../hooks/useAccountAcraStatusMap';
import { clientShowsAcraStatus, withAcraRegisteredAddressFallback } from '../lib/acraRegistry';
import { AccountPurchasePanel } from '../components/accounts/AccountPurchasePanel';
import { AccountCollectionPanel } from '../components/accounts/AccountCollectionPanel';
import { MeetingMinutesModal } from '../components/accounts/MeetingMinutesModal';
import { ContactFormModal } from '../components/contacts/ContactFormModal';
import { ProjectFormModal } from '../components/projects/ProjectFormModal';
import { TaskFormModal } from '../components/tasks/TaskFormModal';
import { TaskDetailModal } from '../components/dashboard/TaskDetailModal';
import { useCrmData } from '../contexts/CrmDataContext';
import { usePortal, usePortalPath } from '../contexts/PortalContext';
import { accountDetailPath } from '../lib/portal';
import { isTempAccountCode } from '../lib/accountCode';
import { CRM_TASK_SAVE_SLICES } from '../lib/crmDataRefresh';
import { completeTaskAndMarkVisited } from '../lib/taskVisitationSync';
import { useMaxWidth } from '../hooks/useMaxWidth';
import type { Contact, Project, Task } from '../types/crm';
import { buildAccountActivity } from '../utils/accountActivity';
import { formatTransferScope } from '../lib/accountTransferScope';
import { isRootAccount, isSubCompany, parentAccount, subCompaniesForParent, clientIdsInAccountScope } from '../utils/accountHierarchy';
import { dedupeContactsForAccountView } from '../utils/contactDedup';
import { accountTypeBadgeStyle, accountTypeShortLabel, idsMatch, userCanAccessClient, userCanWriteClient, userCanWriteOwnedRecord, formatClientSalespersonCode } from '../utils/crmHelpers';

export function AccountDetailPage() {
  const { kind: portalKind } = usePortal();
  const isItPortal = portalKind === 'it';
  const accountsPath = usePortalPath('accounts');
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const {
    data,
    effectiveRole,
    realRole,
    username,
    refresh,
    markTaskDoneLocal,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
    ownerLabel,
  } = useCrmData();
  /** Market Intelligence is still in development — IT only. */
  const canSeeMarketIntel =
    realRole === 'it' || (departmentSlug ?? '').trim().toLowerCase() === 'it';
  const [editOpen, setEditOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [completingTask, setCompletingTask] = useState(false);
  const [centerTab, setCenterTab] = useState<
    'activity' | 'details' | 'intel' | 'purchase' | 'collection'
  >('activity');
  const isMobileLayout = useMaxWidth(900);
  const [minutesModalOpen, setMinutesModalOpen] = useState(false);
  const [minutesInitialId, setMinutesInitialId] = useState<string | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const location = useLocation();
  const [subCompanyModalOpen, setSubCompanyModalOpen] = useState(false);

  const clientRaw = useMemo(
    () => data.clients.find((c) => c.code === decodeURIComponent(code ?? '')),
    [data.clients, code],
  );
  const { statusMap: acraStatusMap, registeredAddressMap } = useAccountAcraStatusMap(
    clientRaw ? [clientRaw] : [],
  );
  const client = useMemo(
    () =>
      clientRaw
        ? withAcraRegisteredAddressFallback(clientRaw, registeredAddressMap)
        : undefined,
    [clientRaw, registeredAddressMap],
  );
  const acraStatus = client ? acraStatusForClient(client, acraStatusMap) : null;

  useEffect(() => {
    setCenterTab(isMobileLayout ? 'details' : 'activity');
    setMinutesInitialId(null);
  }, [client?.id, isMobileLayout]);

  useEffect(() => {
    if (!isMobileLayout && centerTab === 'details') {
      setCenterTab('activity');
    }
  }, [isMobileLayout, centerTab]);

  useEffect(() => {
    if (!canSeeMarketIntel && centerTab === 'intel') {
      setCenterTab('activity');
    }
  }, [canSeeMarketIntel, centerTab]);

  useEffect(() => {
    const state = location.state as { openMinutes?: boolean; minuteId?: string | null } | null;
    if (!client || !state?.openMinutes) return;
    setMinutesModalOpen(true);
    setMinutesInitialId(state.minuteId ?? null);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [client?.id, location.pathname, location.search, location.state, navigate]);

  const canViewDetail = userCanAccessClient(
    client,
    effectiveRole,
    username,
    data.users,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
  );

  const relatedProjects = useMemo(
    () =>
      canViewDetail && client
        ? data.projects.filter((p) => idsMatch(p.client_id, client.id))
        : [],
    [canViewDetail, data.projects, client],
  );

  const relatedContacts = useMemo(() => {
    if (!canViewDetail || !client) return [];
    const scopeIds = new Set(clientIdsInAccountScope(data.clients, client));
    const scoped = data.contacts.filter((c) => scopeIds.has(String(c.client_id)));
    return dedupeContactsForAccountView(scoped, data.clients, client);
  }, [canViewDetail, data.contacts, data.clients, client]);

  const relatedTasks = useMemo(() => {
    if (!canViewDetail || !client) return [];
    return data.tasks
      .filter((t) => idsMatch(t.client_id, client.id))
      .sort((a, b) => {
        const aDone = a.status === 'Done' ? 1 : 0;
        const bDone = b.status === 'Done' ? 1 : 0;
        if (aDone !== bDone) return aDone - bDone;
        return (a.due_date ?? '').localeCompare(b.due_date ?? '');
      });
  }, [canViewDetail, data.tasks, client]);

  const relatedMinutes = useMemo(() => {
    if (!canViewDetail || !client) return [];
    return data.meetingMinutes
      .filter((m) => idsMatch(m.client_id, client.id))
      .sort((a, b) => {
        const da = a.meeting_date ?? a.created_at ?? '';
        const db = b.meeting_date ?? b.created_at ?? '';
        return db.localeCompare(da);
      });
  }, [canViewDetail, data.meetingMinutes, client]);

  function openMinutes(minuteId: string | null) {
    setMinutesInitialId(minuteId);
    setMinutesModalOpen(true);
  }

  const activityItems = useMemo(
    () => (client && canViewDetail ? buildAccountActivity(data, client) : []),
    [client, canViewDetail, data],
  );

  const parent = useMemo(
    () => (client ? parentAccount(data.clients, client) : undefined),
    [client, data.clients],
  );

  const subCompanies = useMemo(
    () => (client && isRootAccount(client) ? subCompaniesForParent(data.clients, client.id) : []),
    [client, data.clients],
  );

  if (!client) {
    return (
      <>
        <p className="empty-state">Account not found.</p>
        <Link to={accountsPath}>← Back to accounts</Link>
      </>
    );
  }

  if (!canViewDetail) {
    return <AccountAccessDenied client={client} />;
  }

  const salespersonCode = formatClientSalespersonCode(client.owner_id);
  const canEdit = userCanWriteClient(client, effectiveRole, actorSalespersonCodes, departmentSlug);
  const canRequestTransfer = canEdit && !isItPortal;
  const pendingTransfer = pendingTransferForClient(data.transferRequests, client.id);
  const typeBadge = accountTypeShortLabel(
    (isSubCompany(client) ? parent?.client_type : client.client_type) || client.client_type,
  );
  const typeBadgeStyle = accountTypeBadgeStyle(
    (isSubCompany(client) ? parent?.client_type : client.client_type) || client.client_type,
  );
  const purchaseFocus =
    (centerTab === 'purchase' || centerTab === 'collection') && !isMobileLayout;

  return (
    <div className="account-detail-page">
      <Link to={accountsPath} className="back-link">
        ← Accounts
      </Link>

      <header className="detail-header detail-header--with-logo account-detail-header">
        <AccountLogoUpload client={client} canEdit={canEdit} onUpdated={() => void refresh()} />
        <div className="detail-header__text">
          <p className="detail-eyebrow">
            {isSubCompany(client) && parent ? (
              <>
                Sub-company ·{' '}
                <Link to={accountDetailPath(parent.code)}>{parent.company_name}</Link>
              </>
            ) : (
              'Account'
            )}
          </p>
          <div className="account-detail-header__title-row">
            <h1 className="page-title">{client.company_name}</h1>
            <span
              className="account-detail-header__type"
              style={typeBadgeStyle}
            >
              {typeBadge === '—' ? 'Account' : typeBadge}
            </span>
            {clientShowsAcraStatus(client) ? (
              <AccountAcraStatus
                client={client}
                prefetchedStatus={acraStatus}
                persist
                className="account-detail-header__acra-status"
              />
            ) : null}
          </div>
          <p className="page-lead">
            {client.code}
            {isTempAccountCode(client.code) && (
              <span className="account-list-row__sub-badge"> Temporary code</span>
            )}
            {isItPortal ? (
              <>
                {' · '}
                {activityItems.length} activities
              </>
            ) : (
              <>
                {' · '}Salesperson: <span className="mono">{salespersonCode}</span> ·{' '}
                {activityItems.length} activities
              </>
            )}
          </p>
        </div>
        <div className="account-detail-header__actions">
          {canRequestTransfer && !pendingTransfer && (
            <button type="button" className="btn-secondary" onClick={() => setTransferOpen(true)}>
              Transfer account
            </button>
          )}
          {canEdit && (
            <button type="button" className="btn-primary" onClick={() => setEditOpen(true)}>
              Edit profile
            </button>
          )}
        </div>
      </header>

      {pendingTransfer && (
        <div className="inline-notice inline-notice--pending">
          <span className="inline-notice__icon" aria-hidden>
            ⏳
          </span>
          <span>
            Transfer pending approval (
            <span className="mono">{pendingTransfer.from_owner_id || '—'}</span> →{' '}
            <span className="mono">{pendingTransfer.to_owner_id || '—'}</span>
            ). Scope: {formatTransferScope(pendingTransfer)}.
          </span>
        </div>
      )}

      {!canEdit && effectiveRole === 'user' && (
        <div className="inline-notice inline-notice--readonly">
          <span className="inline-notice__icon" aria-hidden>
            ℹ
          </span>
          <span>
            Read-only — this account is not assigned to your salesperson code. Contact your supervisor
            to edit.
          </span>
        </div>
      )}

      {isRootAccount(client) && (
        <AccountSubCompaniesPanel
          parent={client}
          subCompanies={subCompanies}
          canEdit={canEdit}
          onAdd={() => setSubCompanyModalOpen(true)}
        />
      )}

      <div
        className={[
          'account-detail-layout',
          'account-detail-layout--three-col',
          purchaseFocus ? 'account-detail-layout--purchase-focus' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <AccountDetailSidebar
          client={client}
          salespersonCode={salespersonCode}
          hideSalesperson={isItPortal}
          contacts={relatedContacts}
          canEdit={canEdit}
          acraStatus={acraStatus}
          onAddContact={() => {
            setEditingContact(null);
            setContactModalOpen(true);
          }}
          onEditContact={(c) => {
            setEditingContact(c);
            setContactModalOpen(true);
          }}
        />

        <main className="account-detail-col account-detail-col--center">
          <section className="account-center-workspace card">
            <header className="account-center-workspace__tabs" role="tablist" aria-label="Account workspace">
              {isMobileLayout ? (
                <button
                  type="button"
                  role="tab"
                  aria-selected={centerTab === 'details'}
                  className={`account-center-workspace__tab${centerTab === 'details' ? ' account-center-workspace__tab--active' : ''}`}
                  onClick={() => setCenterTab('details')}
                >
                  Details
                </button>
              ) : null}
              <button
                type="button"
                role="tab"
                aria-selected={centerTab === 'activity'}
                className={`account-center-workspace__tab${centerTab === 'activity' ? ' account-center-workspace__tab--active' : ''}`}
                onClick={() => setCenterTab('activity')}
              >
                Activity timeline
                <span className="account-center-workspace__tab-count">{activityItems.length}</span>
              </button>
              {canSeeMarketIntel ? (
                <button
                  type="button"
                  role="tab"
                  aria-selected={centerTab === 'intel'}
                  className={`account-center-workspace__tab${centerTab === 'intel' ? ' account-center-workspace__tab--active' : ''}`}
                  onClick={() => setCenterTab('intel')}
                >
                  Market Intelligence
                </button>
              ) : null}
              <button
                type="button"
                role="tab"
                aria-selected={centerTab === 'purchase'}
                className={`account-center-workspace__tab${centerTab === 'purchase' ? ' account-center-workspace__tab--active' : ''}`}
                onClick={() => setCenterTab('purchase')}
              >
                Purchase
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={centerTab === 'collection'}
                className={`account-center-workspace__tab${centerTab === 'collection' ? ' account-center-workspace__tab--active' : ''}`}
                onClick={() => setCenterTab('collection')}
              >
                Collection
              </button>
            </header>

            <div className="account-center-workspace__body" role="tabpanel">
              {centerTab === 'activity' ? (
                <div className="account-timeline-panel__scroll account-center-workspace__scroll">
                  <AccountActivityFeed
                    key={client.id}
                    items={activityItems}
                    previewLimit={isMobileLayout ? 3 : undefined}
                  />
                </div>
              ) : centerTab === 'intel' && canSeeMarketIntel ? (
                <div className="account-center-workspace__scroll account-center-workspace__scroll--intel">
                  <AccountIntelPanel client={client} canRefreshIntel={canViewDetail} />
                </div>
              ) : centerTab === 'purchase' ? (
                <div className="account-center-workspace__scroll account-center-workspace__scroll--purchase">
                  <AccountPurchasePanel motherCode={client.code} companyName={client.company_name} />
                </div>
              ) : centerTab === 'collection' ? (
                <div className="account-center-workspace__scroll account-center-workspace__scroll--collection">
                  <AccountCollectionPanel motherCode={client.code} companyName={client.company_name} />
                </div>
              ) : (
                <div className="account-center-workspace__scroll account-center-workspace__scroll--details">
                  <AccountDetailProfile
                    client={client}
                    salespersonCode={salespersonCode}
                    hideSalesperson={isItPortal}
                    acraStatus={acraStatus}
                  />
                </div>
              )}
            </div>
          </section>
        </main>

        <aside className="account-detail-col account-detail-col--right">
          <AccountDeliveriesPanel client={client} />
          <AccountDetailRelatedLists
            client={client}
            projects={relatedProjects}
            tasks={relatedTasks}
            minutes={relatedMinutes}
            canEdit={canEdit}
            onOpenMinutes={openMinutes}
            onAddProject={() => {
              setEditingProject(null);
              setProjectModalOpen(true);
            }}
            onEditProject={(p) => {
              setEditingProject(p);
              setProjectModalOpen(true);
            }}
            onAddTask={() => {
              setEditingTask(null);
              setTaskModalOpen(true);
            }}
            onOpenTask={(t) => setDetailTask(t)}
          />
        </aside>
      </div>

      <AccountFormModal
        open={editOpen}
        client={client}
        onClose={() => setEditOpen(false)}
        onSaved={(result) => {
          if (result.codeChanged) {
            navigate(accountDetailPath(result.code), { replace: true });
          }
        }}
      />

      <AccountFormModal
        open={subCompanyModalOpen}
        parentClient={client}
        onClose={() => setSubCompanyModalOpen(false)}
      />

      <AccountTransferModal
        open={transferOpen}
        client={client}
        currentOwnerRef={client.owner_id}
        subCompanyCount={subCompanies.length}
        onClose={() => setTransferOpen(false)}
        onSubmitted={() => void refresh()}
      />

      <ContactFormModal
        open={contactModalOpen}
        contact={editingContact}
        defaultClientId={client.id}
        lockClient
        onClose={() => {
          setContactModalOpen(false);
          setEditingContact(null);
        }}
      />

      <ProjectFormModal
        open={projectModalOpen}
        project={editingProject}
        defaultClientId={client.id}
        lockClient
        onClose={() => {
          setProjectModalOpen(false);
          setEditingProject(null);
        }}
      />

      <TaskDetailModal
        open={Boolean(detailTask)}
        task={detailTask}
        client={client}
        meetingMinute={
          detailTask?.meeting_minute_id
            ? relatedMinutes.find((m) => m.id === detailTask.meeting_minute_id) ?? null
            : null
        }
        ownerName={detailTask ? ownerLabel(detailTask.owner_id) : '—'}
        canEdit={
          detailTask
            ? userCanWriteOwnedRecord(
                detailTask.owner_id,
                effectiveRole,
                username,
                data.users,
                supervisorTeam,
                {
                  departmentSlug,
                  clientId: detailTask.client_id,
                  clients: data.clients,
                  actorSalespersonCodes,
                },
              )
            : false
        }
        completing={completingTask}
        onClose={() => setDetailTask(null)}
        onEdit={() => {
          if (!detailTask) return;
          setEditingTask(detailTask);
          setDetailTask(null);
          setTaskModalOpen(true);
        }}
        onDone={() => {
          void (async () => {
            if (!detailTask) return;
            setCompletingTask(true);
            try {
              await completeTaskAndMarkVisited(detailTask);
              markTaskDoneLocal(detailTask.id);
              void refresh({ silent: true, slices: CRM_TASK_SAVE_SLICES });
              setDetailTask(null);
            } catch (err) {
              window.alert(err instanceof Error ? err.message : 'Could not complete task.');
            } finally {
              setCompletingTask(false);
            }
          })();
        }}
      />

      <TaskFormModal
        open={taskModalOpen}
        task={editingTask}
        defaultClientId={client.id}
        lockClient
        onClose={() => {
          setTaskModalOpen(false);
          setEditingTask(null);
        }}
      />

      <MeetingMinutesModal
        open={minutesModalOpen}
        onClose={() => setMinutesModalOpen(false)}
        client={client}
        canEdit={canEdit}
        initialMinuteId={minutesInitialId}
      />
    </div>
  );
}
